import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Video, Play, Clock, Eye, BookOpen, Sparkles } from "lucide-react";

const FornecedorTutorials = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  const tutorials = [
    {
      id: 1,
      title: "Como Cadastrar Produtos",
      description: "Passo a passo completo para cadastrar produtos no catalogo do ClickHub",
      url: "https://www.youtube.com/watch?v=EnTKOjd9P2s",
      thumbnail: "https://img.youtube.com/vi/EnTKOjd9P2s/maxresdefault.jpg",
      category: "Produtos",
      duration: "12:30",
      views: 890,
      level: "Iniciante"
    },
    {
      id: 2,
      title: "Gerenciando Estoque",
      description: "Como controlar estoque e sincronizar automaticamente com os marketplaces",
      url: "https://www.youtube.com/watch?v=WJc0rcWYJog",
      thumbnail: "https://img.youtube.com/vi/WJc0rcWYJog/maxresdefault.jpg",
      category: "Estoque",
      duration: "10:15",
      views: 670,
      level: "Intermediario"
    },
    {
      id: 3,
      title: "Sistema de Comissoes para Afiliados",
      description: "Configure e gerencie comissoes dos seus produtos para afiliados",
      url: "https://www.youtube.com/watch?v=paRCord4bN0",
      thumbnail: "https://img.youtube.com/vi/paRCord4bN0/maxresdefault.jpg",
      category: "Financeiro",
      duration: "8:45",
      views: 1120,
      level: "Intermediario"
    },
    {
      id: 4,
      title: "Emissao de Notas Fiscais",
      description: "Como emitir NF-e diretamente pelo sistema",
      url: "https://www.youtube.com/shorts/cUWFY5zxLzs",
      thumbnail: "https://img.youtube.com/vi/cUWFY5zxLzs/maxresdefault.jpg",
      category: "Fiscal",
      duration: "15:20",
      views: 560,
      level: "Avancado"
    },
    {
      id: 5,
      title: "Gerenciamento de Pedidos",
      description: "Acompanhe e gerencie todos os pedidos dos marketplaces em um so lugar",
      url: "https://www.youtube.com/shorts/D_jEPpPnO90",
      thumbnail: "https://img.youtube.com/vi/D_jEPpPnO90/maxresdefault.jpg",
      category: "Pedidos",
      duration: "11:40",
      views: 780,
      level: "Iniciante"
    },
    {
      id: 6,
      title: "Relatorios e Analises",
      description: "Como usar os relatorios para tomar melhores decisoes de negocio",
      url: "https://www.youtube.com/watch?v=zqKVYB0rH-s",
      thumbnail: "https://img.youtube.com/vi/zqKVYB0rH-s/maxresdefault.jpg",
      category: "Analise",
      duration: "9:30",
      views: 920,
      level: "Avancado"
    }
  ];

  const categories = ["Todos", "Produtos", "Estoque", "Pedidos", "Financeiro", "Fiscal", "Análise"];
  const [selectedCategory, setSelectedCategory] = useState("Todos");

  const filteredTutorials = selectedCategory === "Todos" 
    ? tutorials 
    : tutorials.filter(t => t.category === selectedCategory);

  const getYouTubeEmbedUrl = (url) => {
    const videoId = url.split("v=")[1]?.split("&")[0] || url.split("/").pop();
    return `https://www.youtube.com/embed/${videoId}`;
  };

  const getLevelColor = (level) => {
    switch(level) {
      case "Iniciante": return "bg-green-500/20 text-green-400";
      case "Intermediário": return "bg-yellow-500/20 text-yellow-400";
      case "Avançado": return "bg-red-500/20 text-red-400";
      default: return "bg-gray-500/20 text-gray-400";
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="inline-block mb-3">
          <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
            Tutoriais para Fornecedores
          </span>
        </div>
        <h1 className="text-5xl md:text-6xl font-bold mb-4">
          <span className="bg-gradient-to-r from-white via-blue-200 to-cyan-200 bg-clip-text text-transparent">
            Central de Aprendizado
          </span>
        </h1>
        <p className="text-xl text-gray-300 max-w-3xl">
          Domine todas as funcionalidades do sistema com nossos tutoriais especializados
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 border-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-sm mb-1">Total de Tutoriais</p>
              <p className="text-white text-3xl font-bold">{tutorials.length}</p>
            </div>
            <Video className="w-8 h-8 text-white/80" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 p-4 border-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-sm mb-1">Horas de Conteúdo</p>
              <p className="text-white text-3xl font-bold">1h 8m</p>
            </div>
            <Clock className="w-8 h-8 text-white/80" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 border-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-sm mb-1">Total de Views</p>
              <p className="text-white text-3xl font-bold">4.9k</p>
            </div>
            <Eye className="w-8 h-8 text-white/80" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 border-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white/80 text-sm mb-1">Categorias</p>
              <p className="text-white text-3xl font-bold">6</p>
            </div>
            <BookOpen className="w-8 h-8 text-white/80" />
          </div>
        </Card>
      </div>

      {/* Categoria Filters */}
      <div className="mb-8 flex flex-wrap gap-2">
        {categories.map((cat) => (
          <Button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            variant={selectedCategory === cat ? "default" : "outline"}
            className={selectedCategory === cat 
              ? "bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600" 
              : "border-gray-700 text-gray-300 hover:bg-gray-800"
            }
          >
            {cat}
          </Button>
        ))}
      </div>

      {/* Tutorials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTutorials.map((tutorial) => (
          <Card 
            key={tutorial.id} 
            className="bg-gray-800/50 border-gray-700 overflow-hidden hover:border-blue-500 transition-all cursor-pointer group"
            onClick={() => setSelectedVideo(tutorial)}
          >
            {/* Thumbnail */}
            <div className="relative h-48 bg-gray-900 overflow-hidden">
              <img 
                src={tutorial.thumbnail} 
                alt={tutorial.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                  <Play className="w-8 h-8 text-white ml-1" />
                </div>
              </div>
              
              {/* Duration Badge */}
              <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs text-white font-semibold">
                {tutorial.duration}
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Badges */}
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-blue-500/20 text-blue-400">
                  {tutorial.category}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${getLevelColor(tutorial.level)}`}>
                  {tutorial.level}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-white mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors">
                {tutorial.title}
              </h3>

              {/* Description */}
              <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                {tutorial.description}
              </p>

              {/* Stats */}
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {tutorial.views.toLocaleString('pt-BR')}
                </span>
                <Button
                  size="sm"
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedVideo(tutorial);
                  }}
                >
                  <Play className="w-4 h-4 mr-1" />
                  Assistir
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <div className="max-w-6xl w-full">
            <Card className="bg-gray-900 border-gray-700">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">{selectedVideo.title}</h2>
                    <p className="text-gray-400">{selectedVideo.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedVideo(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    ✕
                  </Button>
                </div>

                {/* Video Player */}
                <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
                  <iframe
                    src={getYouTubeEmbedUrl(selectedVideo.url)}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>

                {/* Video Info */}
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {selectedVideo.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {selectedVideo.views.toLocaleString('pt-BR')} views
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${getLevelColor(selectedVideo.level)}`}>
                    {selectedVideo.level}
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-12 p-6 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-2">
              Precisa de Suporte Técnico?
            </h3>
            <p className="text-gray-300 mb-4">
              Nossa equipe especializada está disponível para ajudar você com integrações, configurações e qualquer dúvida técnica!
            </p>
            <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
              💬 Falar com Suporte Técnico
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FornecedorTutorials;
